import { WorkbenchUiAccessorType } from 'Core/Workbench'
import { CountdownTimerElement } from 'Layouts/Workbench/InformationElements/CountdownTimer/CountdownTimerElement'
import { QuestionTitleElement } from 'Layouts/Workbench/InformationElements/QuestionTitle/QuestionTitleElement'
import { ContainerType, ContextType, TaskConfigType, TaskMode } from 'Types/Tasks.types'
import { createReactElement } from 'shared/utils/react.utils'
import { ContextHandlerConfigType, IContextHandler } from 'Core/ContextHandler/IContextHandler'
import { AttributeHeaderAlert } from 'Components/tasks/Attributes/AttributeHeaderAlert'
import { TimerStartParams, TimerType } from 'Core/Timer'
import TaskInstruction from 'Components/tasks/TaskInstruction/TaskInstruction'
import { createSidebarIcon } from 'Utils/component.utils'
import UnorderedListOutlined from '@ant-design/icons/lib/icons/UnorderedListOutlined'
import { FeedbackManager } from './FeedbackManager'
import React from 'react'
import { EndExamModalController } from 'Core/ModalCore/Modals/EndExamModalController'
import { SubmitModalController } from 'Core/ModalCore/Modals/SubmitModalController'
import { ViolationsModalController } from 'Core/ModalCore/Modals/ViolationsModalController'
import { ActionModalController } from 'Core/ModalCore/Modals/ActionModalController'
import { RejectOrSkipModalController } from 'Core/ModalCore/Modals/RejectOrSkipModalController'
import { ActionParam } from 'Core/ActionCore/ActionManager'
import notification from 'antd/lib/notification'
import { NotificationPropsType } from 'Types/Tasks.types'
import { ErrorCode } from 'Constants/constants'

export class ExamContextHandler<T extends ContainerType, U> implements IContextHandler<T, U> {
    private config: ContextHandlerConfigType<T, U>
    private feedbackManager: FeedbackManager<T, U>

    public getDefaultModals = () => [
        SubmitModalController.Id,
        RejectOrSkipModalController.Id,
        ActionModalController.Id,
        ViolationsModalController.Id,
        EndExamModalController.Id,
    ]

    constructor(config: ContextHandlerConfigType<T, U>) {
        this.config = config
        this.feedbackManager = new FeedbackManager(config)
    }

    isTrainingMode = (): boolean | undefined => {
        return this.config.getStudio().studioConfig.context.metaData?.isTrainingMode
    }

    private isLastQuestion = () => {
        const { currentQuestion, totalQuestions } =
            this.config.getStudio().studioConfig.context.metaData?.examInfo ?? {}
        return currentQuestion === totalQuestions
    }

    submitAttributes = async () => {
        await this.config.saveTask(false)

        await this.feedbackManager.fetchFeedback()
        if (this.feedbackManager.isFeedbackFetched()) {
            this.config.setReadOnly(true)
            this.config.getStudio().workbench.enqueueUiUpdate(() => {
                this.config.getActionManager()?.removeActions(['SubmitAttribute'])

                this.config.getActionManager()?.addActions([
                    {
                        id: 'SubmitExam',
                        config: {
                            title: this.isLastQuestion() ? 'Submit Exam' : 'Go to Next Question',
                            placement: 'start',
                        },
                    },
                ])
            })
        }
        this.config.updateAttributesUI()
    }

    private addCountdownTimerElement = (workbenchUiAccessor: WorkbenchUiAccessorType) => {
        const studio = this.config.getStudio()
        if (studio.studioConfig.context.type !== ContextType.ExamTask) {
            return
        }
        const timeLimit = studio.studioConfig.context.metaData.examInfo?.timeLimit
        if (!timeLimit) {
            return
        }
        const timerElement = createReactElement(CountdownTimerElement, {
            timeLimit,
            getTimer: () => studio.taskManager.timer,
        })
        workbenchUiAccessor.toolbar.addInfoElement({
            id: 'countdown-timer',
            element: timerElement.element,
            alignment: 'right',
            placement: 'start',
        })
    }

    private addQuestionTitle = (workbenchUiAccessor: WorkbenchUiAccessorType) => {
        const studio = this.config.getStudio()
        if (studio.studioConfig.context.type !== ContextType.ExamTask) {
            return
        }
        const examInfo = studio.studioConfig.context.metaData.examInfo
        const examInfoProps =
            studio.taskConfig.mode === TaskMode.Viewer
                ? undefined
                : {
                      questionCount: {
                          currentQuestion: examInfo?.currentQuestion ?? 0,
                          totalQuestions: examInfo?.totalQuestions ?? 0,
                      },
                  }
        const titleElement = createReactElement(QuestionTitleElement, { ...examInfoProps })
        workbenchUiAccessor.toolbar.addInfoElement({
            id: 'question-title',
            element: titleElement.element,
            alignment: 'center',
            placement: 'start',
        })
    }

    endExam = async () => {
        await this.config.flushPendingAutoSave()
        this.config.getStudio().exitTask()
    }

    public addInfoElements = (workbenchUiAccessor: WorkbenchUiAccessorType) => {
        this.addCountdownTimerElement(workbenchUiAccessor)
        this.addQuestionTitle(workbenchUiAccessor)
        const timerSubscription = this.config.getStudio().taskManager.timer.onStopped(() => {
            this.config.getStudio().taskManager.timer.offStopped(timerSubscription)
            this.endExam()
        })
    }

    public addActions = (config: TaskConfigType<T, U>) => {
        if (config.mode === TaskMode.Viewer) {
            return
        }
        const actions: ActionParam[] = []
        if (this.isTrainingMode()) {
            actions.push({ id: 'SubmitAttribute' })
        } else {
            actions.push({
                id: 'SubmitExam',
                config: {
                    title: this.isLastQuestion() ? 'Submit' : 'Submit & Next',
                },
            })
        }
        actions.push({ id: 'EndExam' })
        this.config.getActionManager()?.addActions(actions)
    }

    public addLeftSidebarItems = (workbenchUiAccessor: WorkbenchUiAccessorType) => {
        // add instructions
        workbenchUiAccessor.leftSidebar.addItem({
            id: 'task-instructions',
            element: createReactElement(TaskInstruction, {
                instructions: this.config.getStudio().taskConfig.assignment.task.instructions,
                title: this.getInstructionTitle(),
            }).element,
            icon: createSidebarIcon(UnorderedListOutlined),
            title: this.getInstructionTitle(),
        })
    }

    public createAttributeHeader = () => {
        if (this.config.getStudio().taskConfig.mode === TaskMode.Viewer) {
            return this.isTrainingMode()
                ? createReactElement(AttributeHeaderAlert, {
                      message: 'Below are all the correct answers with available feedback',
                  }).element
                : createReactElement(React.Fragment, {}).element
        }
        return createReactElement(AttributeHeaderAlert, {}).element
    }

    public getInstructionTitle = () => {
        return 'Exam Instructions'
    }

    public getInformationTitle = () => {
        return 'Exam Information'
    }

    public isReadOnly = () => {
        const mode = this.config.getStudio().taskConfig.mode
        if (mode !== TaskMode.Maker) {
            return true
        }
        return false
    }

    public shouldEnableViolations() {
        return false
    }

    public startTimer = () => {
        const studio = this.config.getStudio()
        if (studio.studioConfig.context.type !== ContextType.ExamTask) {
            return
        }
        const timeLimit = studio.studioConfig.context.metaData.examInfo?.timeLimit
        const timerParams: TimerStartParams = timeLimit
            ? { type: TimerType.CountDown, durationInSec: timeLimit }
            : { type: TimerType.CountUp }
        studio.taskManager.timer.start(timerParams)
    }

    public getFeedback = () => {
        return this.feedbackManager.getFeedback()
    }

    public handleSaveSubmissionError = (
        _errorMessage: string,
        errorCode?: string,
        _onComplete?: (props?: NotificationPropsType) => void
    ) => {
        if (errorCode === ErrorCode.EXAM_ARCHIVED) {
            // Handle exam archived
            notification.error({
                message: 'Exam has been ended by Admin',
                placement: 'bottomLeft',
                duration: 5,
                onClose: () => {
                    // Set flag to indicate exam is archived and exit task
                    this.config.getStudio().exitTask({
                        type: 'error',
                        message: 'Exam has been ended by Admin',
                    })
                },
            })
        }
    }
}
